import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "@radix-ui/react-icons";
import { format } from "date-fns";
import type { UseFormReturn } from "react-hook-form";
import { MediaSelector } from "../media/media-selector";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Icons } from "../ui/icons";

type EventFormValues = {
  title: string;
  description: string;
  imageUrl: string;
  allDay: boolean;
  start: string;
  end: string;
  calendarId: string;
};

type Calendar = {
  id: string;
  name: string;
};

export function EventsModalForm({
  onSubmit,
  form,
  isLoading,
  submitError,
  isModalOpen,
  setIsModalOpen,
  calendars,
}: {
  onSubmit: (data: EventFormValues) => void;
  isLoading: boolean;
  submitError?: string;
  form: UseFormReturn<EventFormValues>;
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  calendars: Calendar[];
}) {
  const allDay = form.watch("allDay");

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Event Details</DialogTitle>
          <DialogDescription>
            Create or edit an event for the calendar.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid gap-4 py-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter event title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter event description"
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="calendarId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Calendar</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select calendar" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {calendars.map((calendar) => (
                          <SelectItem key={calendar.id} value={calendar.id}>
                            {calendar.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="allDay"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>All Day Event</FormLabel>
                      <FormDescription>
                        This event lasts the entire day
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="start"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>
                        Start {allDay ? "Date" : "Date & Time"}
                      </FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              {field.value ? (
                                allDay ? (
                                  format(new Date(field.value), "PPP")
                                ) : (
                                  format(new Date(field.value), "PPP p")
                                )
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={
                              field.value ? new Date(field.value) : undefined
                            }
                            onSelect={(date) => {
                              if (date) {
                                if (allDay) {
                                  // Set to start of day
                                  field.onChange(
                                    new Date(
                                      date.setHours(0, 0, 0, 0),
                                    ).toISOString(),
                                  );
                                } else {
                                  // Preserve time or use current time
                                  const currentDate = field.value
                                    ? new Date(field.value)
                                    : new Date();
                                  date.setHours(
                                    currentDate.getHours(),
                                    currentDate.getMinutes(),
                                    0,
                                    0,
                                  );
                                  field.onChange(date.toISOString());
                                }
                              }
                            }}
                            initialFocus
                          />
                          {!allDay && (
                            <div className="p-3 border-t">
                              <Input
                                type="time"
                                value={
                                  field.value
                                    ? format(new Date(field.value), "HH:mm")
                                    : ""
                                }
                                onChange={(e) => {
                                  const [hours, minutes] =
                                    e.target.value.split(":");
                                  const date = field.value
                                    ? new Date(field.value)
                                    : new Date();
                                  date.setHours(
                                    Number.parseInt(hours),
                                    Number.parseInt(minutes),
                                    0,
                                    0,
                                  );
                                  field.onChange(date.toISOString());
                                }}
                              />
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="end"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>
                        End {allDay ? "Date" : "Date & Time"}
                      </FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              {field.value ? (
                                allDay ? (
                                  format(new Date(field.value), "PPP")
                                ) : (
                                  format(new Date(field.value), "PPP p")
                                )
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={
                              field.value ? new Date(field.value) : undefined
                            }
                            onSelect={(date) => {
                              if (date) {
                                if (allDay) {
                                  // Set to end of day
                                  field.onChange(
                                    new Date(
                                      date.setHours(23, 59, 59, 999),
                                    ).toISOString(),
                                  );
                                } else {
                                  // Preserve time or use current time
                                  const currentDate = field.value
                                    ? new Date(field.value)
                                    : new Date();
                                  date.setHours(
                                    currentDate.getHours(),
                                    currentDate.getMinutes(),
                                    0,
                                    0,
                                  );
                                  field.onChange(date.toISOString());
                                }
                              }
                            }}
                            initialFocus
                          />
                          {!allDay && (
                            <div className="p-3 border-t">
                              <Input
                                type="time"
                                value={
                                  field.value
                                    ? format(new Date(field.value), "HH:mm")
                                    : ""
                                }
                                onChange={(e) => {
                                  const [hours, minutes] =
                                    e.target.value.split(":");
                                  const date = field.value
                                    ? new Date(field.value)
                                    : new Date();
                                  date.setHours(
                                    Number.parseInt(hours),
                                    Number.parseInt(minutes),
                                    0,
                                    0,
                                  );
                                  field.onChange(date.toISOString());
                                }}
                              />
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="imageUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Event Image</FormLabel>
                    <FormControl>
                      <MediaSelector field={field} />
                    </FormControl>
                    <FormDescription>
                      Upload or select an image for the event
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              {submitError && <FormMessage>{submitError}</FormMessage>}
              <Button type="submit" disabled={isLoading}>
                {isLoading && (
                  <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
